import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * §v3 Liquid Glass — the canonical panel primitive.
 *
 * Variants control three things in concert: background tint, blur radius,
 * and shadow stack. Don't try to mix-and-match them yourself — these
 * presets came out of the design pass and any deviation will break the
 * "feels like Apple" thing the user explicitly asked for.
 *
 *   panel    — primary surface for cards + content panels (most common)
 *   raised   — hover / selected / current (one tint up)
 *   rail     — sidebars; uses softer blur so the canvas color leaks
 *   popover  — modals + menus + the command palette; hard blur kills
 *              text bleed-through from the layer below
 *   well     — inset surfaces (input fields, search box, sentiment
 *              tracks); reads as "below the panel" not above
 *   profile  — the ONE place tenant brand color is allowed to tint;
 *              user-profile card in the sidebar
 *
 * Hairline borders and the rim shadow are applied automatically — the
 * rim is what makes glass feel like glass; never strip it.
 */
export type GlassVariant =
  | "panel"
  | "raised"
  | "rail"
  | "popover"
  | "well"
  | "profile";

export interface GlassPanelProps extends HTMLAttributes<HTMLDivElement> {
  variant?: GlassVariant;
  /** Override the default `rounded-g-lg`. Use only if the design spec
   * for this surface is explicitly different (e.g. hero KPI = `g-xl`). */
  rounded?: "g-sm" | "g-md" | "g-lg" | "g-xl" | "g-2xl" | "g-pill";
  /** When true the rim/shadow stack is omitted. Use sparingly — only
   * for nested glass that would otherwise stack rims. */
  bare?: boolean;
  children?: ReactNode;
}

const VARIANT_BG: Record<GlassVariant, string> = {
  panel:   "bg-glass-1 backdrop-blur-med backdrop-saturate-glass",
  raised:  "bg-glass-2 backdrop-blur-med backdrop-saturate-glass",
  rail:    "bg-glass-1 backdrop-blur-soft backdrop-saturate-glass",
  popover: "bg-glass-0 backdrop-blur-hard backdrop-saturate-glass",
  // Inset well is NOT translucent in the same way — it sits inside a
  // panel, so it doesn't get its own backdrop blur (would double-blur
  // the already-blurred parent).
  well:    "bg-glass-3",
  profile: "bg-glass-2 backdrop-blur-med backdrop-saturate-glass",
};

const VARIANT_SHADOW: Record<GlassVariant, string> = {
  panel:   "shadow-rim shadow-panel",
  raised:  "shadow-rim shadow-raised",
  rail:    "shadow-rim shadow-panel",
  popover: "shadow-rim shadow-popover",
  // Insets carry their own shadow that reads as depth-into-the-panel.
  well:    "shadow-inset-well",
  profile: "shadow-rim shadow-panel",
};

const VARIANT_BORDER: Record<GlassVariant, string> = {
  panel:   "border border-glass-hairline",
  raised:  "border border-glass-hairline-strong",
  rail:    "border border-glass-hairline",
  popover: "border border-glass-hairline",
  // Wells skip the hairline — the inset shadow already implies the edge.
  well:    "",
  // Profile card carries a tinted border so the tenant color reads.
  // Tailwind's `/N` opacity syntax doesn't compose with raw oklch()
  // CSS vars, so the tint comes from a custom border-style declared
  // inline on the consuming component (see AppShell sb-profile).
  profile: "border",
};

// Tailwind's purger can only see literal class names, so we map the
// `rounded` prop to a static class string up front instead of using
// template-literal interpolation.
const ROUNDED: Record<NonNullable<GlassPanelProps["rounded"]>, string> = {
  "g-sm":   "rounded-g-sm",
  "g-md":   "rounded-g-md",
  "g-lg":   "rounded-g-lg",
  "g-xl":   "rounded-g-xl",
  "g-2xl":  "rounded-g-2xl",
  "g-pill": "rounded-g-pill",
};

export const GlassPanel = forwardRef<HTMLDivElement, GlassPanelProps>(
  function GlassPanel(
    { variant = "panel", rounded = "g-lg", bare = false, className, children, style, ...rest },
    ref,
  ) {
    // Profile variant tints the border with the tenant accent at 40%
    // alpha. Inline style is the cleanest way to apply alpha to an
    // oklch() var; can't be expressed as a Tailwind utility cleanly.
    const profileStyle: React.CSSProperties | undefined =
      variant === "profile"
        ? {
            borderColor:
              "color-mix(in oklab, var(--tenant-accent) 40%, transparent)",
            ...style,
          }
        : style;
    return (
      <div
        ref={ref}
        className={cn(
          "relative text-fg-1",
          ROUNDED[rounded],
          VARIANT_BG[variant],
          !bare && VARIANT_SHADOW[variant],
          !bare && VARIANT_BORDER[variant],
          className,
        )}
        style={profileStyle}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
